"use client";

import { useState, type ReactNode } from "react";
import { Typography, Section } from "@/components/ui";
import Image from "next/image";

export interface FeatureItem {
  icon: string;
  image: string;
  title: string;
  description: ReactNode;
}

interface FeaturesShowcaseProps {
  features: FeatureItem[];
  title: string;
  titleMobile?: string;
  description: string;
  backgroundColor?: string;
  className?: string;
}

export function FeaturesShowcase({
  features,
  title,
  titleMobile,
  description,
  backgroundColor = "#F8F9FF",
  className = "",
}: FeaturesShowcaseProps) {
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(0);

  return (
    <Section variant="default" className={className} style={{ backgroundColor }}>
      <div className="flex text-center justify-center flex-col gap-3 ">
        <Typography variant="h2" as="h2" className="hidden md:block mx-auto max-w-3xl">
          {title}
        </Typography>
        <Typography variant="h2" as="h2" className="w-[280px] block md:hidden mx-auto">
          {titleMobile ? (
            <span dangerouslySetInnerHTML={{ __html: titleMobile }} />
          ) : (
            title
          )}
        </Typography>
        <Typography variant="body" as="p">
          {description}
        </Typography>
      </div>
      <div className="py-12 flex-row justify-between gap-12 items-center mx-auto w-full md:max-w-[1132px] hidden lg:flex">
        <div className="w-full lg:max-w-[468px] flex flex-col gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              onClick={() => setSelectedFeatureIndex(index)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedFeatureIndex(index);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={selectedFeatureIndex === index}
              aria-label={`Select ${feature.title} feature`}
              className={`flex flex-row items-start gap-4 border rounded-lg p-6 cursor-pointer transition-all duration-300 ease-in-out ${
                selectedFeatureIndex === index ? "border-primary bg-white" : "border-transparent"
              }`}
            >
              <div
                className="w-[35px] h-[35px] flex items-center justify-center flex-shrink-0"
                style={{
                  filter: selectedFeatureIndex === index ? "grayscale(0%)" : "grayscale(100%)",
                  transition: "filter 300ms ease-in-out",
                }}
              >
                <Image
                  src={feature.icon}
                  alt={feature.title}
                  title={feature.title}
                  width={35}
                  height={35}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col gap-3">
                <Typography variant="h3" as="h3" className="text-xl leading-normal font-semibold">
                  {feature.title}
                </Typography>
                <Typography variant="body" as="p" className="text-text-secondary">
                  {feature.description}
                </Typography>
              </div>
            </div>
          ))}
        </div>
        <div className="w-full lg:w-3/5 flex place-content-end">
          <Image
            src={features[selectedFeatureIndex].image}
            alt={features[selectedFeatureIndex].title}
            title={features[selectedFeatureIndex].title}
            width={605}
            height={402}
            className="aspect-auto transition-opacity duration-300 ease-in-out"
          />
        </div>
      </div>
      <div className="flex py-8 flex-col px-1.5 lg:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="flex flex-col items-center bg-white gap-4 border border-[#DCDCDC] rounded-lg p-5 transition-all duration-200"
            >
              <div className="w-[30px] h-[30px] flex items-center justify-center flex-shrink-0">
                <Image
                  src={feature.icon}
                  alt={feature.title}
                  title={feature.title}
                  width={30}
                  height={30}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col gap-2 text-center">
                <Typography variant="h3" as="h3" className="text-xl font-bold">
                  {feature.title}
                </Typography>
                <Typography variant="body" as="p" className="text-text-secondary text-center">
                  {feature.description}
                </Typography>
              </div>
              <Image
                src={feature.image}
                alt={feature.title}
                title={feature.title}
                width={250}
                height={173}
                className="w-full max-w-[250px] mx-auto"
              />
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

